import { useEffect, useState } from "react";
import { Item } from "../core/item";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardIcon,
  FolderArrowDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { Button, Text, useDialogs, useNotifications } from "../ui";
import { useAppItems } from "../hooks/useItems";
import { useContainer } from "../hooks/useContainer";

// Constants for configuration
const DATE_FORMAT_OPTIONS = {
  weekday: "short",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  dayPeriod: "long",
  hour: "numeric",
  hourCycle: "h24",
  minute: "numeric",
  second: "numeric",
} as const;

const LOCALE = "de-DE";
const COPY_SUCCESS_MESSAGE = "Url set to clipboard.";
const DOWNLOAD_MESSAGE = "Item is downloaded.";

interface ItemTableProps {
  items: Item[] | undefined;
  onRemoveItem?: (name: string) => void;
}

// Table header with sorting functionality
const TableHeader = ({ dateDesc, onSortByDate }: {
  dateDesc: boolean;
  onSortByDate: () => void;
}) => (
  <thead>
    <tr>
      <th scope="col" className="text-left text-sm text-gray-900 py-2 px-1" style={{ width: 30 }}>
        #
      </th>
      <th scope="col" className="text-left text-sm text-gray-900 py-2 px-1 font-semibold">
        Name
      </th>
      <th
        scope="col"
        className="text-left text-sm text-gray-900 py-2 px-1 cursor-pointer"
        onClick={onSortByDate}
      >
        <span className="flex items-center">
          {dateDesc ? (
            <ChevronDownIcon className="h-5 w-5 mr-2" />
          ) : (
            <ChevronUpIcon className="h-5 w-5 mr-2" />
          )}
          Created
        </span>
      </th>
      <th scope="col" className="text-right text-sm text-gray-900 py-2 px-1">
        Actions
      </th>
    </tr>
  </thead>
);

// Action buttons for each table row
const ItemActions = ({ item, onCopy, onRemove }: {
  item: Item;
  onCopy: (url: string) => void;
  onRemove: (name: string) => void;
}) => {
  const { show } = useNotifications();
  const ACTION_CLASSES = "bg-black text-white p-1 rounded-md hover:bg-gray-500 cursor-pointer";

  return (
    <div className="flex justify-end w-min  gap-1">
      <a
        href={item.url}
        onClick={() => show(DOWNLOAD_MESSAGE)}
        className={ACTION_CLASSES}
      >
        <FolderArrowDownIcon className="h-5 w-5" />
      </a>
      <button onClick={() => onCopy(item.url)} className={ACTION_CLASSES}>
        <ClipboardIcon className="h-5 w-5" />
      </button>
      <button onClick={() => onRemove(item.name)} className={ACTION_CLASSES}>
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

// Individual table row component
const ItemRow = ({ item, onCopy, onRemove }: {
  item: Item;
  onCopy: (url: string) => void;
  onRemove: (name: string) => void;
}) => (
  <tr className="hover:bg-gray-100 cursor-pointer">
    <td className="text-left text-sm text-gray-900 py-2 px-1 font-semibold">
      {item.index}
    </td>
    <td className="text-left text-sm text-gray-900 py-2 px-1 font-semibold">
      {item.name}
    </td>
    <td className="text-left text-sm text-gray-900 py-2 px-1 text-nowrap">
      {item.createdOn.toLocaleString(LOCALE, DATE_FORMAT_OPTIONS)}
    </td>
    <td className="text-left text-sm text-gray-900 py-2 px-1 sticky right-0 z-10 float-right">
      <ItemActions item={item} onCopy={onCopy} onRemove={onRemove} />
    </td>
  </tr>
);

// Hook for table functionality
const useItemTable = () => {
  const { show } = useNotifications();
  const { fetchItems, removeItem, items = [] } = useAppItems();
  const { confirm } = useDialogs();
  const [dateDesc, setDateDesc] = useState(true);
    const { status } = useContainer();

  useEffect(() => {
    if (status === "ready") {
      console.log()
      fetchItems();
    }}, [status])


  // Sort items by date
  const handleSortByDate = () => {
    const sorted = [...items].sort((a: Item, b: Item) => 
      dateDesc 
        ? Number(a.createdOn) - Number(b.createdOn)
        : Number(b.createdOn) - Number(a.createdOn)
    );
    setDateDesc(!dateDesc);
    return sorted;
  };

  // Remove item with confirmation
  const handleRemove = async (itemName: string) => {
    const confirmed = await confirm("Remove Item", `Are you sure to remove item ${itemName}?`, "warning");
    if (confirmed) {
      removeItem(itemName);
      show(`Item ${itemName} removed.`);
    }
  };

  // Copy URL to clipboard
  const handleCopy = (itemUri: string) => {
    navigator.clipboard.writeText(itemUri);
    show(COPY_SUCCESS_MESSAGE);
  };

  return {
    items,
    dateDesc,
    fetchItems,
    handleSortByDate,
    handleRemove,
    handleCopy,
  };
};

// Main item table component for displaying and managing items
export const ItemTable = () => {
  const { items, dateDesc, fetchItems, handleSortByDate, handleRemove, handleCopy } = useItemTable();

  return (
    <section className="mt-4 relative">
      <div className="flex justify-between items-center mb-2">
        <Text variant="label" align="left">Items</Text>
        <Button onClick={fetchItems} size="sm" variant="secondary">
          Refresh
        </Button>
      </div>

      <div className="overflow-x-auto overflow-y-hidden w-full relative scrollbar-thumb-slate-400 scrollbar scrollbar-h-1.5 scrollbar-thumb-rounded-xl">
        <table className="divide-y divide-gray-300 w-full">
          <TableHeader dateDesc={dateDesc} onSortByDate={handleSortByDate} />
          <tbody className="divide-y divide-gray-200 w-full">
            {items.map((item: Item) => (
              <ItemRow
                key={item.index}
                item={item}
                onCopy={handleCopy}
                onRemove={handleRemove}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
